import { getLocale } from "@circle-react/helpers/localeHelpers";
import type {
  PaywallCurrency,
  paywallCurrencySmallestUnits,
} from "@circle-react/types";
import { paywallCurrencyDirections } from "@circle-react/types";

export interface FormatPaywallCurrencyAmountOptions {
  amountInCents: boolean;
  forceMaxFractionDigits: boolean;
  includeCurrencyCode: boolean;
  includeCurrencySymbol: boolean;
}

const precisionDigits: Record<
  keyof typeof paywallCurrencySmallestUnits,
  number
> = {
  decimal: 2,
  zero_decimal: 0,
  three_decimals: 3,
};

export const amountInUnits = (
  currency: Pick<PaywallCurrency, "smallest_unit">,
  amount: number,
) => {
  if (Number.isInteger(amount)) {
    return amount;
  }

  return Math.round(amount * 10 ** precisionDigits[currency.smallest_unit]);
};

const RTL_FORMAT = "{code} {formattedAmountValue}{unit}";
const LTR_FORMAT = "{unit}{formattedAmountValue} {code}";

const customCurrencyFormat: Record<string, string> = {
  sek: "{formattedAmountValue}{unit} {code}",
};

// IMPORTANT: Maintain feature parity with `app/models/currency.rb#format_amount`.
export const formatPaywallCurrencyAmount = (
  currency: Pick<
    PaywallCurrency,
    "smallest_unit" | "code" | "symbol" | "direction"
  >,
  amount: number,
  options: Partial<FormatPaywallCurrencyAmountOptions> = {},
) => {
  const selectedLocale = getLocale(window?.locale || "en");
  const defaultOpts = {
    amountInCents: true,
    includeCurrencyCode: true,
    includeCurrencySymbol: true,
    forceMaxFractionDigits: false,
  };

  const smallestUnit = currency["smallest_unit"] || "decimal";
  const opts = { ...defaultOpts, ...options };

  const amountValue = opts.amountInCents
    ? amount / 10 ** precisionDigits[smallestUnit]
    : amount;

  const precision =
    opts.forceMaxFractionDigits || amountValue % 1 !== 0
      ? precisionDigits[smallestUnit]
      : 0;

  const formattedAmountValue = new Intl.NumberFormat(selectedLocale, {
    style: "decimal",
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  }).format(amountValue);

  const code = opts.includeCurrencyCode ? currency.code.toUpperCase() : "";
  const unit = opts.includeCurrencySymbol ? currency.symbol : "";

  const isRTL = currency.direction === paywallCurrencyDirections.rtl;
  const fallbackFormat = isRTL ? RTL_FORMAT : LTR_FORMAT;

  const formatString = customCurrencyFormat[currency.code] ?? fallbackFormat;

  return formatString
    .replace("{code}", code)
    .replace("{unit}", unit)
    .replace("{formattedAmountValue}", formattedAmountValue)
    .trim();
};
